import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import useTranslation from '@/hooks/useTranslation';

// Anti-silent-loss visibility for the auto-summarization pipeline.
//
// When a long conversation crosses ~70% of the model's context window the
// backend folds the older portion into a rolling summary so the chat can
// keep going forever (see `ContextWindowStrategies.summarize`). Without
// this pill the user would have no idea that any compaction happened —
// they'd just notice the model "forgetting" the early part of their
// strategy session. The pill makes the compaction explicit, names how many
// messages were folded in, and lets curious users expand the summary text
// to verify what was preserved.

const createStyles = (colors, isXS, messageWidth) => StyleSheet.create({
  container: {
    paddingHorizontal: isXS ? 16 : 0,
    paddingVertical: 8,
    alignSelf: 'center',
    width: '100%',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW :
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 16,
    backgroundColor: colors.solid_color_tertiary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    gap: 10,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textWrap: {
    flex: 1,
    flexShrink: 1,
  },
  primaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  expandedSummary: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
});

function ConversationSummaryPill({
  summary,
  summarizedMessageCount,
  messageWidth,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const styles = useMemo(
    () => createStyles(colors, isXS, messageWidth),
    [colors, isXS, messageWidth]
  );

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Hide entirely when there's nothing to surface. Defensive — the parent
  // already gates on `chat.summary`, but a future caller could pass empty.
  const hasSummary = typeof summary === 'string' && summary.trim().length > 0;
  if (!hasSummary) return null;

  const count = Number.isFinite(summarizedMessageCount) ? summarizedMessageCount : 0;
  // Translation lookup with safe fallback so this works even if the en
  // translations file hasn't been synced yet for this key.
  const labelTemplate = t('chat.summaryPill.label') || '%COUNT% earlier message%PLURAL% condensed to keep this chat going.';
  const expandLabel = t('chat.summaryPill.expand') || 'View summary';
  const collapseLabel = t('chat.summaryPill.collapse') || 'Hide summary';
  const labelText = labelTemplate
    .replace('%COUNT%', String(count))
    .replace('%PLURAL%', count === 1 ? '' : 's');

  return (
    <View style={styles.container}>
      <Pressable
        onPress={handleToggle}
        accessibilityRole="button"
        accessibilityLabel={isExpanded ? collapseLabel : expandLabel}
        style={styles.pill}
      >
        <View style={styles.iconWrap}>
          <Icon name="layer-group" size={14} color={colors.text_color_secondary} />
        </View>
        <View style={styles.textWrap}>
          <View style={styles.primaryRow}>
            <Text size="s" color="secondary" numberOfLines={isExpanded ? undefined : 2}>
              {labelText}
            </Text>
            <Text size="s" weight="medium" color="primary">
              {isExpanded ? collapseLabel : expandLabel}
            </Text>
          </View>
        </View>
        <Icon
          name={isExpanded ? 'chevron-up' : 'chevron-down'}
          size={12}
          color={colors.text_color_tertiary}
        />
      </Pressable>
      {isExpanded && (
        <View style={styles.expandedSummary}>
          <Text size="s" color="primary">{summary.trim()}</Text>
        </View>
      )}
    </View>
  );
}

ConversationSummaryPill.propTypes = {
  summary: PropTypes.string,
  summarizedMessageCount: PropTypes.number,
  messageWidth: PropTypes.string,
};

export default React.memo(ConversationSummaryPill);
